import { useState, useCallback } from 'react';
import { api } from '../api/client';
import type { CrawlStatus, CrawlJob } from '../types';

export type CrawlType = 'properties' | 'market_data' | 'full';

interface ApifyCrawlOptions {
  location?: string;
  offerTypes?: string[];
  providers?: string[];
  maxResultsPerProvider?: number;
  deduplicateResults?: boolean;
}

interface CrawlOptions {
  zipCodes?: string[];
  maxZips?: number;
  crawlType?: CrawlType;
  crawlOptions?: ApifyCrawlOptions;
}

interface UseCrawlResult {
  status: CrawlStatus | null;
  loading: boolean;
  error: string | null;
  triggerCrawl: (market: string, budget: number, options?: CrawlOptions) => Promise<void>;
  refreshStatus: () => Promise<void>;
  stopCrawl: (jobId: string) => Promise<void>;
}

export function useCrawl(): UseCrawlResult {
  const [status, setStatus] = useState<CrawlStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      const data = await api.getCrawlStatus();
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch crawl status:', err);
    }
  }, []);

  const triggerCrawl = useCallback(async (market: string, budget: number, options?: CrawlOptions) => {
    setLoading(true);
    setError(null);
    try {
      await api.triggerCrawl(market, budget, options);
      // Refresh status after triggering
      await refreshStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger crawl');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [refreshStatus]);

  const stopCrawl = useCallback(async (jobId: string) => {
    try {
      await api.cancelCrawlJob(jobId);
      // Refresh status after cancelling
      await refreshStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel crawl');
      throw err;
    }
  }, [refreshStatus]);

  return { status, loading, error, triggerCrawl, refreshStatus, stopCrawl };
}

export function getJobStatusColor(status: CrawlJob['status']): string {
  switch (status) {
    case 'completed': return 'var(--success)';
    case 'running': return 'var(--accent)';
    case 'failed': return 'var(--error)';
    default: return 'var(--text-secondary)';
  }
}

export function formatJobTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return date.toLocaleDateString();
}
